import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Wrench, Package, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { FileUploadZone } from '../FileUploadZone';
import { getStatusBadge } from './lotStatusStyles';

export const LotTabs = ({
  lot,
  activeTab,
  onActiveTabChange,
  simplifiedActive,
  simplifiedProgress,
  progress,
  workPhases,
  controls,
  ncs,
  revisionTools,
  deliveries,
  attachments,
  hasRole,
  onOpenSimpleDialog,
  onDeleteSimpleProgress,
  onOpenProgressDialog,
  onOpenDeliveryDialog,
  onDeleteDelivery,
  onLoadAttachments,
  lotId,
}) => {
  return (
    <Tabs value={activeTab} onValueChange={onActiveTabChange}>
      <TabsList className="h-12 flex-wrap">
        {simplifiedActive && (
          <TabsTrigger value="simplified" className="text-base px-6 h-10" data-testid="tab-simplified">Semplificato ({simplifiedProgress.length})</TabsTrigger>
        )}
        <TabsTrigger value="progress" className="text-base px-6 h-10">Avanzamento ({progress.length})</TabsTrigger>
        <TabsTrigger value="controls" className="text-base px-6 h-10">Controlli ({controls.length})</TabsTrigger>
        <TabsTrigger value="ncs" className="text-base px-6 h-10">NC ({ncs.length})</TabsTrigger>
        <TabsTrigger value="tools" className="text-base px-6 h-10">Utensili ({revisionTools.length})</TabsTrigger>
        <TabsTrigger value="deliveries" className="text-base px-6 h-10">Consegne ({deliveries.length})</TabsTrigger>
        <TabsTrigger value="attachments" className="text-base px-6 h-10">Allegati ({attachments.length})</TabsTrigger>
      </TabsList>

      {/* Simplified Progress Tab */}
      <TabsContent value="simplified" className="mt-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Avanzamento Semplificato
            </CardTitle>
            {hasRole('admin', 'quality', 'production', 'operator') && (
              <Button onClick={onOpenSimpleDialog} className="h-11 px-5 bg-amber-600 hover:bg-amber-700" data-testid="add-simplified-progress-btn">
                <Plus className="w-5 h-5 mr-2" />
                Registra Avanzamento Semplificato
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {simplifiedProgress.length === 0 ? (
              <p className="text-center text-slate-500 py-8 italic">Nessun avanzamento semplificato registrato</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Prodotto</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Consegnato</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">DDT</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Note</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {simplifiedProgress.map(sp => (
                      <tr key={sp.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`simplified-row-${sp.id}`}>
                        <td className="px-3 py-2 font-medium">{sp.progress_date}</td>
                        <td className="px-3 py-2 text-center font-bold text-emerald-700">{sp.produced_quantity}</td>
                        <td className="px-3 py-2 text-center font-bold text-sky-700">{sp.delivered_quantity}</td>
                        <td className="px-3 py-2 font-mono text-slate-700">{sp.ddt_reference || '-'}</td>
                        <td className="px-3 py-2 text-slate-500 truncate max-w-[220px]">{sp.notes || '-'}</td>
                        <td className="px-3 py-2 text-right">
                          {hasRole('admin', 'quality', 'production') && (
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                              onClick={() => onDeleteSimpleProgress(sp.id)} data-testid={`delete-simplified-${sp.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="px-3 py-2 font-bold text-slate-700">Totali</td>
                      <td className="px-3 py-2 text-center font-bold text-emerald-800 text-base">{simplifiedProgress.reduce((s, p) => s + (p.produced_quantity || 0), 0)}</td>
                      <td className="px-3 py-2 text-center font-bold text-sky-800 text-base">{simplifiedProgress.reduce((s, p) => s + (p.delivered_quantity || 0), 0)}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Standard Progress Tab */}
      <TabsContent value="progress" className="mt-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Avanzamento Lotto</CardTitle>
            {hasRole('admin', 'quality', 'production', 'operator') && workPhases.length > 0 && (
              <Button onClick={onOpenProgressDialog} className="h-11 px-5" data-testid="add-progress-btn">
                <Plus className="w-5 h-5 mr-2" />
                Registra Avanzamento
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {progress.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No progress recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Phase</th>
                      <th>Operator</th>
                      <th>Machine</th>
                      <th>Good</th>
                      <th>Scrap</th>
                      <th>Rework</th>
                      <th>Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progress.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <span className="font-mono">{p.phase_number}</span> - {p.phase_description}
                        </td>
                        <td>{p.operator || '-'}</td>
                        <td>{p.machine || '-'}</td>
                        <td className="text-emerald-600 font-medium">{p.good_quantity}</td>
                        <td className="text-rose-600 font-medium">{p.scrap_quantity}</td>
                        <td className="text-amber-600 font-medium">{p.rework_quantity}</td>
                        <td>{p.actual_time_minutes ? `${p.actual_time_minutes} min` : '-'}</td>
                        <td>
                          <Badge className={getStatusBadge(p.status)}>{p.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Controls Tab */}
      <TabsContent value="controls" className="mt-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Registro Controlli Qualità ({controls.length})</CardTitle>
            <div className="flex gap-2">
              <Link to="/qms-checks">
                <Button variant="outline" className="h-11 px-5">Apri Registro completo</Button>
              </Link>
              {hasRole('admin', 'quality', 'production', 'operator') && (
                <Link to="/qms-checks">
                  <Button className="h-11 px-5" data-testid="add-control-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Registra Controllo
                  </Button>
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {controls.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nessun controllo registrato per il lotto {lot?.lot_code}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Data/Ora</th>
                      <th>Fase</th>
                      <th>Finalità</th>
                      <th>Caratteristica</th>
                      <th>Risultato</th>
                      <th>Esito</th>
                      <th>Metodo/Strumento</th>
                      <th>Controllore</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {controls.map((c) => {
                      const isNc = c.esito_controllo === 'Non conforme' || c.esito_controllo === 'Lotto bloccato';
                      const firstRow = (c.row_results || [])[0];
                      return (
                        <tr key={c.id} className={isNc ? 'bg-red-50 border-l-4 border-l-red-500' : ''} data-testid={`lot-control-row-${c.id}`}>
                          <td className="font-mono text-xs">{(c.data_ora_controllo || '').slice(0, 16).replace('T', ' ')}</td>
                          <td>{c.fase_processo}</td>
                          <td>{c.finalita_controllo}</td>
                          <td className="max-w-xs">
                            {firstRow ? (
                              <>
                                <p className="text-xs font-medium">{firstRow.descrizione_caratteristica}</p>
                                {(c.row_results || []).length > 1 && <p className="text-xs text-slate-500">+ {(c.row_results.length - 1)} altre</p>}
                              </>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="font-mono text-xs">{firstRow?.risultato_misurato || '—'}</td>
                          <td>
                            <Badge className={
                              c.esito_controllo === 'Conforme' ? 'bg-emerald-500 text-white px-3 py-1' :
                              isNc ? 'bg-red-600 text-white px-3 py-1' :
                              'bg-amber-500 text-white px-3 py-1'
                            }>
                              {c.esito_controllo}
                            </Badge>
                            {c.plan_obbligatorie_nc > 0 && <Badge className="ml-1 bg-red-100 text-red-700 border border-red-300 text-[10px]">{c.plan_obbligatorie_nc} obbl NC</Badge>}
                          </td>
                          <td className="text-xs">{c.metodo_strumento}{c.strumento_codice ? ` · ${c.strumento_codice}` : ''}</td>
                          <td className="text-xs">{c.controllore || '—'}</td>
                          <td>
                            <Link to="/qms-checks">
                              <Button size="sm" variant="ghost">Dettagli</Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* NCs Tab */}
      <TabsContent value="ncs" className="mt-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Non Conformità</CardTitle>
            <Link to="/non-conformities">
              <Button variant="outline" className="h-11 px-5">Vedi Tutte le NC</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {ncs.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nessuna non conformità per questo lotto</p>
            ) : (
              <div className="space-y-3">
                {ncs.map((nc) => (
                  <Link key={nc.id} to={`/non-conformities/${nc.id}`} className="block" data-testid={`nc-link-${nc.id}`}>
                    <div className="flex items-center justify-between p-5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200">
                      <div className="flex-1">
                        <p className="font-mono font-semibold text-base">{nc.nc_number}</p>
                        <p className="text-sm text-slate-500 truncate max-w-[400px] mt-1">{nc.defect_description}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={`nc-${nc.nc_type}`}>{nc.nc_type}</Badge>
                        <Badge className={`severity-${nc.severity}`}>{nc.severity}</Badge>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Tools Tab */}
      <TabsContent value="tools" className="mt-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Utensili Richiesti dalla Revisione</CardTitle>
          </CardHeader>
          <CardContent>
            {revisionTools.length === 0 ? (
              <div className="text-center py-8">
                <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nessun utensile richiesto per questa revisione</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Seq.</th>
                      <th>Codice</th>
                      <th>Descrizione</th>
                      <th>Fase</th>
                      <th>Funzione</th>
                      <th>Obbl.</th>
                      <th>Crit.</th>
                      <th>Stato Utensile</th>
                      <th>Note Setup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revisionTools.map((rt) => (
                      <tr key={rt.id}>
                        <td className="font-mono font-semibold">{rt.sequence}</td>
                        <td className="font-mono text-sm">{rt.tool_code}</td>
                        <td>{rt.tool_description}</td>
                        <td className="text-sm">{rt.phase_description || '-'}</td>
                        <td className="text-sm">{rt.tool_function}</td>
                        <td>{rt.mandatory ? <Badge className="bg-emerald-100 text-emerald-700">Si</Badge> : '-'}</td>
                        <td>{rt.critical ? <Badge className="bg-red-100 text-red-700">Critico</Badge> : '-'}</td>
                        <td>
                          <Badge className={rt.current_status === 'available' ? 'bg-emerald-500 text-white' : rt.current_status === 'in_use' ? 'bg-amber-500 text-white' : 'bg-slate-400 text-white'}>
                            {rt.current_status}
                          </Badge>
                        </td>
                        <td className="text-xs max-w-[200px] truncate">{rt.setup_notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Deliveries Tab */}
      <TabsContent value="deliveries" className="mt-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Consegne</CardTitle>
            {lot?.order_line_id && (
              <Button size="sm" onClick={onOpenDeliveryDialog} data-testid="new-delivery-btn">
                <Plus className="w-4 h-4 mr-1" /> Registra Consegna
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!lot?.order_line_id ? (
              <p className="text-sm text-slate-500 italic">Questo lotto non è collegato a una riga ordine. Le consegne possono essere registrate solo per lotti con ordine associato.</p>
            ) : deliveries.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nessuna consegna registrata per questo lotto.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Qtà Consegnata</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">DDT</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Ordine</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Cliente</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Note</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map(d => (
                      <tr key={d.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium">{d.delivery_date}</td>
                        <td className="px-3 py-2 text-center font-bold text-emerald-700">{d.delivered_quantity}</td>
                        <td className="px-3 py-2 font-mono text-slate-700">{d.ddt_reference || '-'}</td>
                        <td className="px-3 py-2">{d.order_number || '-'}</td>
                        <td className="px-3 py-2">{d.client_name || '-'}</td>
                        <td className="px-3 py-2 text-slate-500 truncate max-w-[180px]">{d.notes || '-'}</td>
                        <td className="px-3 py-2 text-right">
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                            onClick={() => onDeleteDelivery(d.id)} data-testid={`delete-delivery-${d.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="px-3 py-2 font-bold text-slate-700">Totale consegnato</td>
                      <td className="px-3 py-2 text-center font-bold text-emerald-800 text-base">{deliveries.reduce((s, d) => s + d.delivered_quantity, 0)}</td>
                      <td colSpan={5}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Attachments Tab */}
      <TabsContent value="attachments" className="mt-6">
        <FileUploadZone
          entityType="production_lot"
          entityId={lotId}
          attachments={attachments}
          onUploadComplete={onLoadAttachments}
          onDeleteComplete={onLoadAttachments}
          canEdit={hasRole('admin', 'quality', 'production', 'operator')}
        />
      </TabsContent>
    </Tabs>
  );
};
